import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from "react-redux"; 
import { usePracticeById } from '../../hooks/Practice/usePracticeById';
import { 
  UserPlus, 
  Mail, 
  User, 
  Send,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import useCognitoSignUp from "../../hooks/Auth/useCognitoSignUp";
import { RootState } from "../../redux/index";

interface InviteFormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

interface InviteCoordinatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (userData: any) => void;
}

const InviteCoordinatorModal: React.FC<InviteCoordinatorModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { selectedPractice } = useSelector((state: RootState) => state.practices);
  const { handleCreateUser, loading, error } = useCognitoSignUp();
  const { fetchPracticeDetails } = usePracticeById();

  // State management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState<InviteFormData>({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [confirmEmail, setConfirmEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Get practice ID
  const practiceId = useMemo(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      return userData?.["custom:id_practice"] || '';
    } catch {
      return '';
    }
  }, []);

  // Fetch practice details on mount
  useEffect(() => {
    if (practiceId && !selectedPractice && isOpen) {
      fetchPracticeDetails(practiceId);
    }
  }, [practiceId, selectedPractice, fetchPracticeDetails, isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!confirmEmail.trim()) {
      newErrors.confirmEmail = 'Please confirm your email';
    } else if (formData.email !== confirmEmail) {
      newErrors.confirmEmail = 'Email addresses do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, confirmEmail]);

  // Handle input changes  
  const handleInputChange = useCallback((field: keyof InviteFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for the field being updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  // Create user account
  const createUserAccount = useCallback(async () => {
    if (!selectedPractice?._id) {
      throw new Error('Practice information is required');
    }

    const userDataForCognito = {
      given_name: formData.firstName,
      family_name: formData.lastName,
      email: formData.email,
      role: 'intake_coordinator',
      status: 'pending',
      practice_id: selectedPractice._id,
      password: 'Password1234!'
    };

    await handleCreateUser(userDataForCognito);
    return { userData: userDataForCognito };
  }, [formData, selectedPractice, handleCreateUser]);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      const result = await createUserAccount();
      setIsSuccess(true);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Auto close after 2 seconds
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
      
    } catch (error) {
      let errorMessage = 'Failed to send invitation. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('email')) {
          errorMessage = 'There was an issue with the email address. Please verify it\'s correct and try again.';
        } else if (error.message.includes('Practice information')) {
          errorMessage = 'Practice information is missing. Please refresh the page and try again.';
        } else {
          errorMessage = error.message;
        }
      }
        
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, createUserAccount, onClose, onSuccess]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      firstName: '',
      lastName: '',
      email: ''
    });
    setConfirmEmail('');
    setErrors({});
    setIsSuccess(false);
    setIsSubmitting(false);
  }, []);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      setTimeout(resetForm, 300);
    }
  }, [isSubmitting, onClose, resetForm]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isSubmitting, handleClose]);

  // Add a useMemo hook to check if the form is valid
  const isFormValid = useMemo(() => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      confirmEmail.trim() !== '' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
      formData.email === confirmEmail &&
      Object.keys(errors).length === 0
    );
  }, [formData, confirmEmail, errors]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
          />
          
          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          >
            {/* Success Screen */}
            {isSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Invitation Sent Successfully!
                </h2>
                <p className="text-gray-600 mb-6">
                  An invitation has been sent to <strong>{formData.email}</strong>. 
                  They will receive an email with instructions to join your team.
                </p>
                <p className="text-sm text-gray-500">
                  This modal will close automatically...
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="border-b border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <UserPlus className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Invite Intake Coordinator</h2>
                        <p className="text-sm text-gray-600">Send an invitation to join your team</p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      disabled={isSubmitting}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                      aria-label="Close modal"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
                  {/* Error Display */}
                  {errors.submit && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                        <p className="text-red-700">{errors.submit}</p>
                      </div>
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    <div>
                      <div className="flex items-center mb-4">
                        <User className="w-4 h-4 text-gray-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                            First Name * 
                          </label>
                          <input
                            id="firstName"
                            name="firstName"
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                              errors.firstName ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="Enter first name"
                            aria-invalid={!!errors.firstName}
                            aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                          />
                          {errors.firstName && (
                            <p id="firstName-error" className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.firstName}
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                            Last Name *
                          </label>
                          <input
                            id="lastName"
                            name="lastName"
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                              errors.lastName ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="Enter last name"
                            aria-invalid={!!errors.lastName}
                            aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                          />
                          {errors.lastName && (
                            <p id="lastName-error" className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.lastName}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center mb-4">
                        <Mail className="w-4 h-4 text-gray-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">Email</h3>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address *
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                              errors.email ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="Enter email address"
                            aria-invalid={!!errors.email}
                            aria-describedby={errors.email ? 'email-error' : undefined}
                          />
                          {errors.email && (
                            <p id="email-error" className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.email}
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="confirmEmail" className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Email *
                          </label>
                          <input
                            id="confirmEmail"
                            name="confirmEmail"
                            type="email"
                            value={confirmEmail}
                            onChange={(e) => {
                              setConfirmEmail(e.target.value);
                              if (errors.confirmEmail) {
                                setErrors(prev => ({ ...prev, confirmEmail: undefined }));
                              }
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                              errors.confirmEmail ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="Confirm email address"
                            aria-invalid={!!errors.confirmEmail}
                            aria-describedby={errors.confirmEmail ? 'confirmEmail-error' : undefined}
                          />
                          {errors.confirmEmail && (
                            <p id="confirmEmail-error" className="mt-1 text-sm text-red-600 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              {errors.confirmEmail}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-6">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!isFormValid || isSubmitting || loading}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {isSubmitting || loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Invitation
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default InviteCoordinatorModal;